import React, { useState, useEffect, useCallback, useMemo } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { VisitRoute, VisitStatus } from "../../../types/visit";
import { DataTableColumn, SimpleFilter } from "../../../types";
import { Calendar, User, Eye, MapPin, Search } from "lucide-react";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { CustomDateInput } from "../../../components/CustomDateInput";
import { Tag } from "primereact/tag";
import { IconButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { useVisitManagementStore } from "../../../store/visitStore";
import { useEmployeeStore } from "../../../store/employeeStore";
import { VisitDetailDialog } from "./VisitDetailDialog";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { Employee } from "../../../types/employee";

export const VisitMonitorList = () => {
    const { routes, monitorPlasiyer } = useVisitManagementStore();
    const employeeStore = useEmployeeStore();

    const [selectedDateRange, setSelectedDateRange] = useState<(Date | null)[] | null>([new Date(), new Date()]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
    const [employeeName, setEmployeeName] = useState("");

    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState<VisitRoute | null>(null);

    const employeeSelect = useSelectDialog<Employee>();

    // Plasiyer Seçme
    const openEmployeeSelect = async () => {
        const res = await employeeStore.fetchDataDynamic({ pageIndex: 1, pageSize: 10 });
        const selected = await employeeSelect.open({
            title: "Personel Seç",
            data: res?.items || [],
            searchFields: [
                { field: "firstName", operator: "contains" },
                { field: "lastName", operator: "contains" }
            ],
            columns: [
                { field: "firstName", header: "Ad" },
                { field: "lastName", header: "Soyad" }
            ],
            onFilter: async (p) => {
                const r = await employeeStore.fetchDataDynamic({
                    pageIndex: p.pageIndex,
                    pageSize: p.pageSize,
                    filter: p.filter
                });
                return { data: r.items, total: r.total };
            }
        });

        if (selected) {
            setSelectedEmployeeId(selected.id);
            setEmployeeName(`${selected.firstName} ${selected.lastName}`);
        }
    };

    // Seçim veya Tarih değişince veriyi store üzerinden çek
    useEffect(() => {
        if (selectedEmployeeId) {
            let startDate: string | undefined;
            let endDate: string | undefined;
            let date: string | undefined;

            if (selectedDateRange && Array.isArray(selectedDateRange)) {
                if (selectedDateRange[0]) startDate = new Date(selectedDateRange[0]).toISOString().split('T')[0];
                if (selectedDateRange[1]) endDate = new Date(selectedDateRange[1]).toISOString().split('T')[0];
            } else if (selectedDateRange) {
                // single date fallback
                date = new Date(selectedDateRange as Date).toISOString().split('T')[0];
            }

            // Eğer range modunda henüz 2. tarih seçilmemişse arama yapmayalım
            if (Array.isArray(selectedDateRange) && selectedDateRange[0] && !selectedDateRange[1]) {
                return;
            }

            console.log("Sorgu atılıyor:", selectedEmployeeId, date, startDate, endDate);

            monitorPlasiyer(selectedEmployeeId, date, startDate, endDate)
                .then(res => {
                    console.log("Gelen Veri:", res);
                });
        }
    }, [selectedEmployeeId, selectedDateRange]);

    const handleShowDetail = useCallback((route: VisitRoute) => {
        setSelectedVisit(route);
        setIsDetailOpen(true);
    }, []);

    const getStatusSeverity = useCallback((status: VisitStatus) => {
        switch (status) {
            case 'COMPLETED': return 'success';
            case 'IN_PROGRESS': return 'info';
            case 'PENDING': return 'warning';
            case 'CANCELLED': return 'danger';
            case 'POSTPONED': return 'secondary';
            default: return null;
        }
    }, []);

    const columns: DataTableColumn<VisitRoute>[] = useMemo(() => [
        { field: "visitOrder", header: "Sıra", width: "80px", sortable: true },
        {
            field: "account.name",
            header: "Müşteri / Adres",
            body: (r) => (
                <div className="flex flex-col">
                    <span className="font-bold">{r.account?.name}</span>
                    <span className="text-xs flex items-center gap-1 opacity-70">
                        <MapPin className="w-3 h-3" /> {r.address?.title || "Merkez"}
                    </span>
                </div>
            ),
            width: "350px"
        },
        {
            field: "status",
            header: "Durum",
            body: (r) => <Tag value={r.status} severity={getStatusSeverity(r.status)} />,
            width: "150px"
        },
        {
            field: "isOffRoute",
            header: "Tip",
            body: (r) => r.isOffRoute ? <Tag value="Dış Rota" severity="warning" /> : <Tag value="Planlı" severity="success" />,
            width: "150px"
        },
        {
            field: "actions",
            header: "Aksiyonlar",
            body: (r) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.actions && r.actions.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {(r.actions?.length || 0)} İşlem
                </span>
            ),
            width: "120px"
        },
        {
            field: "view",
            header: "İncele",
            body: (r) => (
                <TableActions
                    actions={[
                        {
                            icon: Eye,
                            colorClass: "text-blue-500 hover:bg-blue-50",
                            title: "İncele",
                            onClick: () => handleShowDetail(r)
                        }
                    ]}
                />
            ),
            width: "100px",
            align: "center"
        }
    ], [handleShowDetail, getStatusSeverity]);

    const routesDataRef = React.useRef(routes.data);
    const routesTotalRef = React.useRef(routes.total);

    useEffect(() => {
        routesDataRef.current = routes.data;
        routesTotalRef.current = routes.total;
    }, [routes.data, routes.total]);

    const mockFetchData = useCallback(async () => {
        return { items: routesDataRef.current, total: routesTotalRef.current };
    }, []);

    const storeAdapter = useCallback(() => {
        return {
            ...routes,
            fetchDataDynamic: mockFetchData
        };
    }, [routes, mockFetchData]);

    return (
        <div className="w-full space-y-4 p-4">
            {/* Filtre Paneli */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 card2 p-4">
                <div className="flex flex-col gap-2">
                    <CustomSelectInput
                        label={<><User size={16} className="text-primary" /> Plasiyer İzleme</>}
                        value={employeeName}
                        placeholder="Plasiyer seçmek için tıklayın..."
                        icon={User}
                        onClick={openEmployeeSelect}
                        onClear={() => {
                            setSelectedEmployeeId(null);
                            setEmployeeName("");
                        }}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <CustomDateInput
                        label={<><Calendar size={16} className="text-primary" /> İzleme Tarihi</>}
                        value={selectedDateRange}
                        onChange={(val) => {
                            // Sadece geçerli bir tarih aralığı veya tarih geldiğinde güncelle
                            if (val && val[0]) {
                                setSelectedDateRange(val);
                            }
                        }}
                        selectionMode="range"
                    />
                </div>
            </div>

            {selectedEmployeeId ? (
                <div className="card2">
                    <EDataTable<VisitRoute>
                        tableKey="visit_monitor_list"
                        dataKey="id"
                        // Store'u doğrudan bağlıyoruz, monitorPlasiyer store'daki 'data'yı güncellediği için tablo beslenir
                        useStore={storeAdapter}
                        title={`${employeeName} - Saha Performansı`}
                        columns={columns as any}
                        searchFields={[
                            { field: "account.name", operator: "contains" as SimpleFilter["operator"] },
                        ]}
                        placeholder="Müşteri Adı Ara..."

                    />
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center p-20 card2 border-dashed border-2">
                    <div className="p-4 bg-primary/10 rounded-full mb-4">
                        <Search size={40} className="text-primary" />
                    </div>
                    <p className="text-lg font-bold">Takip Başlatılmadı</p>
                    <p className="text-sm text-center opacity-60 max-w-xs">
                        Lütfen yukarıdan bir plasiyer ve tarih seçerek saha verilerini anlık olarak izlemeye başlayın.
                    </p>
                </div>
            )}

            <VisitDetailDialog
                isOpen={isDetailOpen}
                onClose={() => setIsDetailOpen(false)}
                visit={selectedVisit}
            />

            {employeeSelect.modal}
        </div>
    );
};